
import React from 'react';
import { Headphones, Shuffle } from 'lucide-react';
import { getBrainwaveCategory, getBrainwaveCategoryColor } from './FrequencySlider';

export interface PresetInfo {
  id: string;
  name: string;
  frequency: number;
  duration: number;
  description: string;
  color: string;
  isLucky?: boolean;
  ambientSounds?: Array<{ id: string; volume: number }>;
  advancedSettings?: {
    leftFrequency: number;
    rightFrequency: number;
    carrierFrequency: number;
    leftWaveform?: string;
    rightWaveform?: string;
  };
}

interface PresetCardProps {
  preset: PresetInfo;
  isActive: boolean;
  onClick: (preset: PresetInfo) => void;
}

const PresetCard: React.FC<PresetCardProps> = ({ 
  preset, 
  isActive,
  onClick 
}) => {
  const { name, frequency, duration, description, color, isLucky, ambientSounds, advancedSettings } = preset;

  return (
    <div
      className={`
        relative overflow-hidden glass rounded-2xl p-6 sm:p-7 transition-all duration-300
        border border-white/20 h-[220px] sm:h-[240px] 2xl:h-[260px]
        ${isActive 
          ? 'ring-2 ring-offset-2 ring-offset-background ring-primary scale-[1.02] shadow-lg' 
          : 'hover:scale-[1.01] cursor-pointer shadow-sm hover:shadow-md'
        }
        ${isLucky ? 'bg-gradient-to-br from-[#D946EF] to-[#8B5CF6]' : ''}
      `}
      onClick={() => onClick(preset)}
      style={{ color: isLucky ? '#ffffff' : color }}
    >
      {/* Particle effect for lucky presets */}
      {isLucky && (
        <div className="absolute inset-0 opacity-20">
          {[...Array(20)].map((_, i) => (
            <div 
              key={i}
              className="absolute rounded-full bg-white"
              style={{
                width: `${Math.random() * 10 + 2}px`,
                height: `${Math.random() * 10 + 2}px`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                opacity: Math.random() * 0.5 + 0.1,
                animation: `pulse ${Math.random() * 3 + 2}s infinite ease-in-out`,
                animationDelay: `${Math.random() * 2}s`
              }}
            />
          ))}
        </div>
      )}
      
      <div className="relative z-10 h-full flex flex-col justify-between">
        <div className="space-y-2 mb-auto">
          <h3 className="font-medium text-lg">{name}</h3>
          <p className="text-sm text-black/70 dark:text-white/70">{description}</p>
        </div>
        
        <div className="flex justify-between items-end mt-4">
          <div className="space-y-1.5">
            {isLucky ? (
              <div className="flex items-center gap-1.5">
                <div className="h-2 w-2 rounded-full animate-pulse-slow bg-white"></div>
                <span className="text-xs font-medium">Random settings</span>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1.5">
                  <div className="h-2 w-2 rounded-full animate-pulse-slow" style={{ backgroundColor: color }}></div>
                  <span className="text-xs font-medium">{frequency} Hz</span>
                  {frequency > 0 && (
                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${getBrainwaveCategoryColor(getBrainwaveCategory(frequency))} bg-opacity-20 ml-1`}>
                      {getBrainwaveCategory(frequency)}
                    </span>
                  )}
                </div>
                {ambientSounds && ambientSounds.length > 0 && (
                  <div className="text-xs text-black/70 dark:text-white/70 flex items-center">
                    <span className="mr-1">+</span>
                    <span>{ambientSounds.map(s => s.id.charAt(0).toUpperCase() + s.id.slice(1)).join(', ')}</span>
                  </div>
                )}
              </div>
            )}
            <div className="text-xs text-black/70 dark:text-white/70 pt-1">{isLucky ? 'Random' : duration} minutes</div>
          </div>
          
          <div
            className={`
              h-8 w-8 flex items-center justify-center rounded-full transition-colors
              ${isActive 
                ? 'bg-white text-black' 
                : isLucky 
                  ? 'bg-white/20 text-white' 
                  : 'bg-black/10 text-black dark:bg-white/10 dark:text-white'}
            `}
          >
            {isLucky ? (
              <Shuffle size={16} className={isActive ? '' : 'opacity-70'} />
            ) : (
              <Headphones size={16} className={isActive ? '' : 'opacity-70'} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PresetCard;
